import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';

interface ClientLite {
  id: string;
  firstName: string;
  surname: string;
  dateOfBirth: string | null;
  email1: string | null;
  email2: string | null;
  email3: string | null;
  phoneMobile: string | null;
  phoneHome: string | null;
  addressLine1: string | null;
  suburb: string | null;
  postcode: string | null;
  segment: string | null;
  riskProfile: string | null;
  annualIncomeCents: number | null;
  createdAt: string;
}

interface Candidate {
  id: string;
  primaryClientId: string;
  secondaryClientId: string;
  score: string;             // numeric serialised
  matchedFields: string[];
  state: string;
  createdAt: string;
  primary?: ClientLite;
  secondary?: ClientLite;
  preview?: { fieldsCopied: string[]; conflicts: string[] };
}

const FIELD_LABEL: Record<string, string> = {
  firstName: 'First name',
  surname: 'Surname',
  'firstName~': 'First name (fuzzy)',
  dateOfBirth: 'Date of birth',
  email: 'Email',
  phone: 'Phone',
  addressLine1: 'Address',
  postcode: 'Postcode',
};

const TAB_DEFS = [
  { key: 'open', label: 'Open' },
  { key: 'merged', label: 'Merged' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'auto_merged', label: 'Auto-merged' },
] as const;

export function MatchCandidates() {
  const [tab, setTab] = useState<'open' | 'merged' | 'dismissed' | 'auto_merged'>('open');
  const focusId = new URLSearchParams(window.location.search).get('focus');
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['match-candidates', tab],
    queryFn: () => api<{ data: Candidate[] }>(`/api/admin/match-candidates?state=${tab}`),
  });

  const merge = useMutation({
    mutationFn: ({ id, primaryId }: { id: string; primaryId?: string }) =>
      api(`/api/admin/match-candidates/${id}/merge`, { method: 'POST', body: JSON.stringify(primaryId ? { primaryId } : {}) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['match-candidates'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api(`/api/admin/match-candidates/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['match-candidates'] }),
  });

  if (isLoading) return <QASkeleton variant="cards" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load candidates</div>;

  return (
    <div className="py-6">
      <header className="mb-6">
        <h1 className="qa-h2">Possible <em className="text-rust">duplicates.</em></h1>
        <p className="text-ink-mute text-sm mt-2 max-w-2xl">
          Pairs flagged by the dedupe engine. Identical email + name + DOB are auto-merged silently;
          fuzzier matches land here for review. Click <strong>Merge</strong> to combine (the left side
          survives by default — flip with <em>swap primary</em>), or <strong>Dismiss</strong> if
          they're genuinely different people.
        </p>
      </header>

      <div className="qa-tabs mb-4">
        {TAB_DEFS.map(({ key, label }) => (
          <button key={key} type="button"
            onClick={() => setTab(key)}
            className={`qa-tab ${tab === key ? 'qa-tab--active' : ''}`}
            aria-current={tab === key ? 'page' : undefined}>
            {label}
          </button>
        ))}
      </div>

      {data.data.length === 0 ? (
        <QAEmpty inline title={`No ${tab.replace('_', ' ')} candidates.`} />
      ) : (
        <ul className="space-y-4">
          {data.data.map(c => (
            <CandidateCard
              key={c.id}
              c={c}
              focused={c.id === focusId}
              onMerge={(primaryId) => merge.mutate({ id: c.id, primaryId })}
              onDismiss={() => dismiss.mutate(c.id)}
              busy={merge.isPending || dismiss.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CandidateCard({ c, focused, onMerge, onDismiss, busy }: {
  c: Candidate;
  focused?: boolean;
  onMerge: (primaryId?: string) => void;
  onDismiss: () => void;
  busy: boolean;
}) {
  const scorePct = Math.round(Number(c.score) * 100);
  const [flipped, setFlipped] = useState(false);
  const left = flipped ? c.secondary : c.primary;
  const right = flipped ? c.primary : c.secondary;
  const cardRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (focused) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focused]);

  return (
    <li ref={cardRef} className={`qa-card ${focused ? 'border-rust ring-2 ring-rust/40' : ''}`}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-hairline-paper text-xs qa-mono uppercase">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 ${scorePct >= 80 ? 'bg-rust text-paper' : scorePct >= 60 ? 'bg-brass/20 text-brass-deep' : 'bg-ink-mute/20'}`}>
            {scorePct}% match
          </span>
          <span className="text-ink-mute">matched:</span>
          {c.matchedFields.length === 0
            ? <span className="text-ink-mute italic">(none)</span>
            : c.matchedFields.map(f => (
              <span key={f} className="text-ink-mute">{FIELD_LABEL[f] ?? f}</span>
            ))}
        </div>
        <span className="text-ink-mute">flagged {formatDateAU(c.createdAt)}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-hairline-paper">
        <ClientPanel client={left} side="Primary (will survive)" />
        <ClientPanel client={right} side="Secondary (will be archived)" />
      </div>

      {c.preview && (c.preview.fieldsCopied.length > 0 || c.preview.conflicts.length > 0) && (
        <div className="px-4 py-3 border-t border-hairline-paper bg-parchment/40 text-xs">
          {c.preview.fieldsCopied.length > 0 && (
            <div className="mb-1">
              <span className="text-forest qa-mono uppercase mr-2">copy →</span>
              {c.preview.fieldsCopied.join(', ')}
            </div>
          )}
          {c.preview.conflicts.length > 0 && (
            <div>
              <span className="text-caution qa-mono uppercase mr-2">conflicts</span>
              {c.preview.conflicts.join(', ')} <span className="text-ink-mute">(primary's value wins)</span>
            </div>
          )}
        </div>
      )}

      {c.state === 'open' && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-hairline-paper">
          <button type="button" className="btn-primary text-sm"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Merge these two clients? This will archive the secondary record.')) {
                onMerge(left?.id);
              }
            }}>
            Merge
          </button>
          <button type="button" className="btn-ghost text-sm"
            onClick={() => setFlipped(f => !f)}>
            Swap primary
          </button>
          <button type="button" className="btn-ghost text-sm"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Dismiss this candidate as not-a-duplicate?')) onDismiss();
            }}>
            Dismiss
          </button>
        </div>
      )}
    </li>
  );
}

function ClientPanel({ client, side }: { client?: ClientLite; side: string }) {
  if (!client) return <div className="p-4 text-ink-mute text-sm">Record unavailable</div>;
  return (
    <div className="p-4">
      <div className="text-xs qa-mono uppercase text-ink-mute mb-2">{side}</div>
      <h3 className="qa-display text-xl mb-2">
        {client.firstName} <em>{client.surname}</em>
      </h3>
      <dl className="text-sm grid grid-cols-[120px_1fr] gap-y-1">
        <Row label="DOB" v={client.dateOfBirth ? formatDateAU(client.dateOfBirth) : '—'} />
        <Row label="Email" v={[client.email1, client.email2, client.email3].filter(Boolean).join(', ') || '—'} />
        <Row label="Phone" v={[client.phoneMobile, client.phoneHome].filter(Boolean).join(', ') || '—'} />
        <Row label="Address" v={[client.addressLine1, client.suburb, client.postcode].filter(Boolean).join(', ') || '—'} />
        <Row label="Segment" v={client.segment ?? '—'} />
        <Row label="Risk" v={client.riskProfile ?? '—'} />
        <Row label="Income" v={client.annualIncomeCents != null ? formatAUD(client.annualIncomeCents / 100) : '—'} />
        <Row label="Created" v={formatDateAU(client.createdAt)} />
      </dl>
    </div>
  );
}

function Row({ label, v }: { label: string; v: string }) {
  return (
    <>
      <dt className="text-ink-mute qa-mono uppercase text-xs">{label}</dt>
      <dd className="text-ink">{v}</dd>
    </>
  );
}
